// assets/edm-bundle-state.js
// Single Source of Truth für Bundle-State

window.EdumonBundle = window.EdumonBundle || {};

window.EdumonBundle.State = {
  // Instance state (wird pro Bundle-Builder-Instanz erstellt)
  instances: new Map(),
  
  /**
   * Initialize state for a bundle builder instance
   * @param {string} instanceId - Unique instance ID (usually product ID)
   * @param {Object} config - Configuration object
   * @param {number} config.productId - Product ID
   * @param {Array} config.tiers - Discount tiers
   * @param {number} config.maxSlots - Maximum slots
   * @param {Element} config.el - Bundle builder element
   * @returns {Object} State instance
   */
  init: function(instanceId, config) {
    const { productId, tiers, maxSlots, el } = config;
    
    const state = {
      instanceId: instanceId,
      productId: productId,
      tiers: tiers || [],
      maxSlots: maxSlots || 10,
      el: el,
      
      // State data
      items: {}, // {variantId: qty}
      activeBundleKey: '',
      cartDirty: false,
      
      // Performance optimization: Pre-calculated tierBySlot map for O(1) lookups
      // Context7: Performance - Avoids array.find() in render loops
      tierBySlot: new Map(),
      
      // Getters
      get totalQty() {
        return Object.values(this.items).reduce((a, b) => a + b, 0);
      },
      
      get tier() {
        let t = null;
        for (const ti of this.tiers) {
          if (this.totalQty >= ti.qty) t = ti;
        }
        return t;
      },
      
      // Methods
      setItems: function(items) {
        // Context7: Fail Fast - Validate before setting
        const totalQty = Object.values(items).reduce((a, b) => a + b, 0);
        if (totalQty > this.maxSlots) {
          console.warn('[Bundle Builder] State: Total quantity exceeds maximum, capping to', this.maxSlots);
          this.items = window.EdumonBundle.Utils.capToMaximum(items, this.maxSlots);
        } else {
          this.items = items;
        }
        this.markDirty();
      },
      
      addItem: function(variantId, qty = 1) {
        if (!this.canAddItem(qty)) {
          console.warn('[Bundle Builder] State: Cannot add item, maximum reached');
          return false;
        }
        const vid = String(variantId);
        this.items[vid] = (this.items[vid] || 0) + qty;
        this.markDirty();
        return true;
      },
      
      removeItem: function(variantId, qty = 1) {
        const vid = String(variantId);
        const current = this.items[vid] || 0;
        if (current <= qty) {
          delete this.items[vid];
        } else {
          this.items[vid] = current - qty;
        }
        this.markDirty();
      },
      
      canAddItem: function(qty = 1) {
        return (this.totalQty + qty) <= this.maxSlots;
      },
      
      getCurrentTier: function() {
        return this.tier;
      },
      
      getNextTier: function() {
        return this.tiers.find(t => t.qty > this.totalQty);
      },
      
      /**
       * Cap items to maximum slots
       * Context7: State-specific convenience method that uses this.maxSlots
       * Migrated from bb-state-LEGACY.js
       * 
       * @param {Object} items - Items object {variantId: qty}
       * @returns {Object} Capped items object
       */
      capToMaximum: function(items) {
        return window.EdumonBundle.Utils.capToMaximum(items, this.maxSlots);
      },
      
      /**
       * Set item quantity (convenience method)
       * Context7: Convenience API for single item updates
       * Migrated from bb-state-LEGACY.js
       * 
       * @param {string|number} variantId - Variant ID
       * @param {number} qty - Quantity
       */
      setItemQty: function(variantId, qty) {
        const vid = String(variantId);
        const nextItems = { ...this.items };
        if (qty <= 0) {
          delete nextItems[vid];
        } else {
          nextItems[vid] = qty;
        }
        this.setItems(nextItems);
      },
      
      reset: function() {
        this.items = {};
        this.activeBundleKey = '';
        this.cartDirty = false;
        this.updateDOM();
      },
      
      markDirty: function() {
        this.cartDirty = true;
        this.updateDOM();
      },
      
      markClean: function() {
        this.cartDirty = false;
        this.updateDOM();
      },
      
      updateDOM: function() {
        if (this.el) {
          if (this.activeBundleKey) {
            this.el.setAttribute(window.EdumonBundle.Config.DATA_ATTRS.ACTIVE_BUNDLE_KEY, this.activeBundleKey);
          } else {
            this.el.removeAttribute(window.EdumonBundle.Config.DATA_ATTRS.ACTIVE_BUNDLE_KEY);
          }
          this.el.setAttribute(window.EdumonBundle.Config.DATA_ATTRS.CART_DIRTY, this.cartDirty ? '1' : '0');
        }
      },
      
      setActiveBundleKey: function(key) {
        this.activeBundleKey = key || '';
        this.updateDOM();
      },
      
      /**
       * Load bundle state from cart with strict quantity aggregation
       * Context7: Single Source of Truth - Aggregates quantities from all lines per variant
       * 
       * This function handles split lines correctly by summing quantities for the same variant.
       * Example: Cart has Line A (Vanille x 1) and Line B (Vanille x 2) -> state.items['vanille_id'] = 3
       */
      loadFromCart: async function(bundleGroup) {
        this.items = {};
        if (bundleGroup) {
          // Context7: Strict aggregation - sum all quantities per variant (handles split lines)
          // Iterate over all lines and aggregate quantities by variant ID
          for (const ln of bundleGroup.lines) {
            const vid = String(ln.variantId);
            // Add quantity to existing total (handles multiple lines for same variant)
            this.items[vid] = (this.items[vid] || 0) + ln.qty;
          }
          
          // Context7: Resilience - Self-correct if server data is invalid
          const totalQty = Object.values(this.items).reduce((a, b) => a + b, 0);
          if (totalQty > this.maxSlots) {
            console.warn('[Bundle Builder] loadFromCart: Server data exceeds maximum, capping to', this.maxSlots);
            this.items = window.EdumonBundle.Utils.capToMaximum(this.items, this.maxSlots);
          }
          this.setActiveBundleKey(bundleGroup.bundleKey);
          this.markClean();
        } else {
          // Bundle wurde aus Warenkorb gelöscht: State komplett zurücksetzen
          this.reset();
        }
        return bundleGroup;
      }
    };
    
    // Initialize tierBySlot map for performance optimization
    // Context7: Performance - Pre-calculate tier discounts by slot index for O(1) lookups
    state.tierBySlot.clear();
    state.tiers.forEach(t => {
      if (t.qty <= state.maxSlots) {
        state.tierBySlot.set(t.qty, t.off);
      }
    });
    
    // Initialize
    state.reset();
    
    // Store instance
    this.instances.set(instanceId, state);
    
    return state;
  },
  
  /**
   * Get state instance by ID
   * @param {string} instanceId - Instance ID
   * @returns {Object|null} State instance or null
   */
  get: function(instanceId) {
    return this.instances.get(instanceId) || null;
  },
  
  /**
   * Remove state instance
   * @param {string} instanceId - Instance ID
   */
  remove: function(instanceId) {
    this.instances.delete(instanceId);
  }
};

